import { ref, computed, watch, onMounted } from '@vue/composition-api'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import agendamentosService from '@/services/agendamentos'
import { convertLocalToUTC, convertUTCToLocal } from '@/utils/timezoneConverter'
import {
  WEEK_DAYS, MONTHS, isSameDay, getStartOfWeek, formatDateKey,
  formatDateTimeForPicker, parseDateFromPicker,
} from './calendar-utils'

export default function useCalendarPessoal() {
  const toast = useToast()

  // ------------------------------------------------
  // User
  // ------------------------------------------------
  const getUserId = () => {
    try {
      const executivoId = localStorage.getItem('executivo_id')
      if (executivoId) return parseInt(executivoId)
      const userData = JSON.parse(localStorage.userData || '{}')
      return userData.executivo_cobranca || userData.funcionarioId || userData.id || null
    } catch { return null }
  }

  const usuarioLogadoId = getUserId()

  if (!usuarioLogadoId) {
    toast({
      component: ToastificationContent,
      props: {
        title: 'Erro de autenticação',
        icon: 'AlertTriangleIcon',
        variant: 'danger',
        text: 'Não foi possível identificar o usuário logado. Por favor, faça login novamente.',
      },
    })
  }

  // ------------------------------------------------
  // State
  // ------------------------------------------------
  const currentDate = ref(new Date())
  const view = ref('month')
  const loading = ref(false)
  const agendamentos = ref([])

  // Filters
  const selectedTipos = ref(['NEGOCIACAO', 'CONTATO', 'BOLETO'])
  const selectedStatuses = ref([
    'AGENDADO',
    'PENDENTE',
    'EM_ANDAMENTO',
    'CONCLUIDO',
    'REAGENDADO',
  ])
  const selectedMotivos = ref([])
  const showAllMotivos = ref(true)

  const executivosList = ref([])

  const executivosComAgendamentos = computed(() => {
    const map = new Map()
    // Index executives by id for quick lookup
    const execMap = new Map(executivosList.value.map(e => [e.id, e.nome || e.name]))
    agendamentos.value.forEach(a => {
      if (a.atendente_id && !map.has(a.atendente_id)) {
        const nome = execMap.get(a.atendente_id) || a.atendente?.nome || a.atendente?.name || `Executivo ${a.atendente_id}`
        map.set(a.atendente_id, { label: nome, value: a.atendente_id })
      }
    })
    return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label))
  })

  const deselectedExecutivos = ref([])
  const selectedExecutivos = computed({
    get() {
      return executivosComAgendamentos.value
        .map(e => e.value)
        .filter(id => !deselectedExecutivos.value.includes(id))
    },
    set(val) {
      const currentIds = executivosComAgendamentos.value.map(e => e.value)
      deselectedExecutivos.value = currentIds.filter(id => !val.includes(id))
    },
  })

  // Event sidebar
  const isEventSidebarOpen = ref(false)
  const selectedEvent = ref(null)
  const saving = ref(false)
  const updatingStatus = ref(false)

  // Event form
  const eventForm = ref({
    cliente_id: undefined,
    tipo: 'NEGOCIACAO',
    data_agendamento: '',
    atendente_id: undefined,
    observacoes: '',
    status: 'AGENDADO',
    motivo: '',
  })

  const statusForm = ref({
    status: '',
    motivo: '',
    observacoes: '',
  })

  // Client search
  const clienteSearch = ref('')
  const selectedClienteNome = ref('')
  const clienteSuggestions = ref([])
  const highlightedClienteIndex = ref(-1)
  const isSearchingClientes = ref(false)
  const showClienteSuggestions = ref(false)
  let clienteSearchTimeout = null

  // ------------------------------------------------
  // Computed
  // ------------------------------------------------
  const isNotExecutivoCobranca = computed(() => {
    try {
      const funcao = localStorage.getItem('funcao')
      return funcao !== 'Executivo de Cobrança'
    } catch { return true }
  })

  const currentMonthYear = computed(() => {
    const d = currentDate.value
    return `${MONTHS[d.getMonth()]} ${d.getFullYear()}`
  })

  const weekDays = computed(() => WEEK_DAYS)

  const calendarDays = computed(() => {
    const d = currentDate.value
    const year = d.getFullYear()
    const month = d.getMonth()
    const firstDay = new Date(year, month, 1)
    const lastDay = new Date(year, month + 1, 0)
    const startDay = firstDay.getDay()
    const days = []
    const today = new Date()

    // Previous month days
    for (let i = startDay - 1; i >= 0; i--) {
      const date = new Date(year, month, -i)
      days.push({
        date: date.getDate(),
        fullDate: date,
        dateKey: formatDateKey(date),
        isCurrentMonth: false,
        isToday: isSameDay(date, today),
        month: date.getMonth(),
        year: date.getFullYear(),
      })
    }

    // Current month days
    for (let i = 1; i <= lastDay.getDate(); i++) {
      const date = new Date(year, month, i)
      days.push({
        date: i,
        fullDate: date,
        dateKey: formatDateKey(date),
        isCurrentMonth: true,
        isToday: isSameDay(date, today),
        month,
        year,
      })
    }

    // Next month days
    const remaining = 42 - days.length
    for (let i = 1; i <= remaining; i++) {
      const date = new Date(year, month + 1, i)
      days.push({
        date: date.getDate(),
        fullDate: date,
        dateKey: formatDateKey(date),
        isCurrentMonth: false,
        isToday: isSameDay(date, today),
        month: date.getMonth(),
        year: date.getFullYear(),
      })
    }

    return days
  })

  const weekViewDays = computed(() => {
    const start = getStartOfWeek(currentDate.value)
    const today = new Date()
    return Array.from({ length: 7 }, (_, i) => {
      const date = new Date(start)
      date.setDate(date.getDate() + i)
      return {
        fullDate: date,
        dateKey: formatDateKey(date),
        date: date.getDate(),
        weekDay: WEEK_DAYS[date.getDay()],
        isToday: isSameDay(date, today),
      }
    })
  })

  const filteredEvents = computed(() => {
    let events = agendamentos.value
    events = events.filter(e => {
      const tipo = (e.tipo || '').toUpperCase()
      if (!tipo) return true
      return selectedTipos.value.includes(tipo)
    })
    events = events.filter(e => {
      const status = (e.status || '').toUpperCase()
      if (!status) return true
      return selectedStatuses.value.includes(status)
    })
    if (!showAllMotivos.value) {
      if (selectedMotivos.value.length === 0) {
        events = events.filter(e => !(e.motivo || '').toUpperCase())
      } else {
        events = events.filter(e => {
          const motivo = (e.motivo || '').toUpperCase()
          if (!motivo) return false
          return selectedMotivos.value.includes(motivo)
        })
      }
    }
    if (isNotExecutivoCobranca.value) {
      events = events.filter(e => selectedExecutivos.value.includes(e.atendente_id))
    }
    return events
  })

  const sortedEvents = computed(() => [...filteredEvents.value].sort((a, b) => new Date(a.data_agendamento) - new Date(b.data_agendamento)))

  const executivoOptionsForForm = computed(() => executivosList.value.map(e => ({ label: e.nome || e.name, value: e.id })))

  const fetchExecutivos = async () => {
    try {
      const axiosAuth = (await import('@/../axios-auth')).default
      const response = await axiosAuth.get('api/v1/executivo')
      const data = response.data || []
      executivosList.value = Array.isArray(data) ? data : []
    } catch {
      try {
        const axiosGlobal = (await import('@/libs/axios')).default
        const response = await axiosGlobal.get('api/v1/executivo')
        const data = response.data || []
        executivosList.value = Array.isArray(data) ? data : []
      } catch { /* ignore */ }
    }
  }

  // ------------------------------------------------
  // Fetch
  // ------------------------------------------------
  const fetchAgendamentos = async () => {
    if (!usuarioLogadoId) return
    loading.value = true
    try {
      const d = currentDate.value
      const year = d.getFullYear()
      const month = d.getMonth()
      let inicio
      let fim

      if (view.value === 'month') {
        inicio = new Date(year, month, 1)
        fim = new Date(year, month + 1, 0)
      } else if (view.value === 'week') {
        inicio = getStartOfWeek(d)
        fim = new Date(inicio)
        fim.setDate(fim.getDate() + 6)
      } else {
        inicio = new Date(d)
        fim = new Date(d)
      }

      const params = {
        inicio: inicio.toISOString().split('T')[0],
        fim: fim.toISOString().split('T')[0],
      }

      if (!isNotExecutivoCobranca.value) {
        params.atendente_id = usuarioLogadoId
      }

      const response = await agendamentosService.buscarAgendamentosCalendario(params)
      const rawAgendamentos = response.data?.agendamentos || response.agendamentos || []
      agendamentos.value = rawAgendamentos.map(a => {
        let status = (a.status || '').toUpperCase()
        if (status === 'AGENDAMENTO') status = 'AGENDADO'
        if (status === 'PENDENCIA') status = 'PENDENTE'
        return {
          ...a,
          status,
        }
      })
    } catch (error) {
      toast({
        component: ToastificationContent,
        props: {
          title: 'Erro ao buscar agendamentos',
          icon: 'AlertTriangleIcon',
          variant: 'danger',
          text: error.message || 'Não foi possível carregar os agendamentos',
        },
      })
    } finally {
      loading.value = false
    }
  }

  // ------------------------------------------------
  // Event helpers
  // ------------------------------------------------
  const getEventsForDay = day => {
    const d = day.fullDate || new Date(day.year, day.month, day.date)
    return filteredEvents.value.filter(e => {
      const eventDate = convertUTCToLocal(e.data_agendamento)
      return eventDate && isSameDay(eventDate, d)
    })
  }

  const getEventsForHour = (day, hour) => {
    const d = day.fullDate || new Date(day.year, day.month, day.date)
    return filteredEvents.value.filter(e => {
      const eventDate = convertUTCToLocal(e.data_agendamento)
      return eventDate && isSameDay(eventDate, d) && eventDate.getHours() === hour
    })
  }

  const getEventsForDayHour = hour => {
    const d = currentDate.value
    return filteredEvents.value.filter(e => {
      const eventDate = convertUTCToLocal(e.data_agendamento)
      return eventDate && isSameDay(eventDate, d) && eventDate.getHours() === hour
    })
  }

  // ------------------------------------------------
  // Navigation
  // ------------------------------------------------
  const previousMonth = () => {
    const d = new Date(currentDate.value)
    if (view.value === 'month') d.setMonth(d.getMonth() - 1)
    else if (view.value === 'week') d.setDate(d.getDate() - 7)
    else d.setDate(d.getDate() - 1)
    currentDate.value = d
  }

  const nextMonth = () => {
    const d = new Date(currentDate.value)
    if (view.value === 'month') d.setMonth(d.getMonth() + 1)
    else if (view.value === 'week') d.setDate(d.getDate() + 7)
    else d.setDate(d.getDate() + 1)
    currentDate.value = d
  }

  const goToToday = () => {
    currentDate.value = new Date()
  }

  const openDayView = day => {
    currentDate.value = day.fullDate || new Date(day.year, day.month, day.date)
    view.value = 'day'
  }

  // ------------------------------------------------
  // Filter toggles
  // ------------------------------------------------
  const toggleTipo = (value, checked) => {
    if (checked) {
      if (!selectedTipos.value.includes(value)) {
        selectedTipos.value = [...selectedTipos.value, value]
      }
    } else {
      selectedTipos.value = selectedTipos.value.filter(t => t !== value)
    }
  }

  const toggleStatus = (value, checked) => {
    if (checked) {
      if (!selectedStatuses.value.includes(value)) {
        selectedStatuses.value = [...selectedStatuses.value, value]
      }
    } else {
      selectedStatuses.value = selectedStatuses.value.filter(s => s !== value)
    }
  }

  const toggleMotivo = (value, checked) => {
    showAllMotivos.value = false
    if (checked) {
      if (!selectedMotivos.value.includes(value)) {
        selectedMotivos.value = [...selectedMotivos.value, value]
      }
    } else {
      selectedMotivos.value = selectedMotivos.value.filter(m => m !== value)
    }
  }

  const clearMotivos = () => {
    showAllMotivos.value = true
    selectedMotivos.value = []
  }

  const uncheckAllMotivos = () => {
    showAllMotivos.value = false
    selectedMotivos.value = []
  }

  const toggleExecutivo = (value, checked) => {
    if (checked) {
      if (!selectedExecutivos.value.includes(value)) {
        selectedExecutivos.value = [...selectedExecutivos.value, value]
      }
    } else {
      selectedExecutivos.value = selectedExecutivos.value.filter(e => e !== value)
    }
  }

  // ------------------------------------------------
  // Client search
  // ------------------------------------------------
  const searchClientes = async query => {
    if (!query || query.length < 3) {
      clienteSuggestions.value = []
      showClienteSuggestions.value = false
      return
    }
    isSearchingClientes.value = true
    try {
      const axios = (await import('@/../axios-auth')).default
      const response = await axios.get('/api/v1/clientes/filtro_otimizado', { params: { filtro: query, per_page: 10, page: 1 } })
      clienteSuggestions.value = response.data?.dados?.data || response.data?.dados || []
      showClienteSuggestions.value = clienteSuggestions.value.length > 0
    } catch {
      clienteSuggestions.value = []
      showClienteSuggestions.value = false
    } finally {
      isSearchingClientes.value = false
    }
  }

  watch(clienteSearch, val => {
    if (selectedEvent.value?.id) return
    if (val === selectedClienteNome.value) return

    // User is editing/typing, clear selected client ID
    eventForm.value.cliente_id = undefined

    clearTimeout(clienteSearchTimeout)
    clienteSearchTimeout = setTimeout(() => searchClientes(val), 300)
  })

  const handleClienteSearchFocus = () => {
    if (clienteSearch.value.length >= 3) showClienteSuggestions.value = true
  }

  const handleClienteSearchBlur = () => {
    setTimeout(() => { showClienteSuggestions.value = false }, 200)
  }

  const navigateClienteDown = () => {
    if (highlightedClienteIndex.value < clienteSuggestions.value.length - 1) {
      highlightedClienteIndex.value++
    }
  }

  const navigateClienteUp = () => {
    if (highlightedClienteIndex.value > 0) {
      highlightedClienteIndex.value--
    }
  }

  const selectCliente = cliente => {
    eventForm.value.cliente_id = cliente.id
    clienteSearch.value = cliente.nome || cliente.name || ''
    selectedClienteNome.value = cliente.nome || cliente.name || ''
    clienteSuggestions.value = []
    showClienteSuggestions.value = false
    highlightedClienteIndex.value = -1
  }

  const selectHighlightedCliente = () => {
    if (highlightedClienteIndex.value >= 0 && clienteSuggestions.value[highlightedClienteIndex.value]) {
      const cliente = clienteSuggestions.value[highlightedClienteIndex.value]
      selectCliente(cliente)
    }
  }

  const clearClienteSelection = () => {
    eventForm.value.cliente_id = undefined
    clienteSearch.value = ''
    selectedClienteNome.value = ''
    clienteSuggestions.value = []
    showClienteSuggestions.value = false
  }

  // ------------------------------------------------
  // Event CRUD
  // ------------------------------------------------
  const openEventSidebar = (event = null) => {
    if (event && event.id) {
      selectedEvent.value = event
      eventForm.value = {
        cliente_id: event.cliente_id,
        tipo: event.tipo || 'NEGOCIACAO',
        data_agendamento: formatDateTimeForPicker(convertUTCToLocal(event.data_agendamento)),
        atendente_id: event.atendente_id,
        observacoes: event.observacoes || '',
        status: event.status || 'AGENDADO',
        motivo: event.motivo || '',
      }
      clienteSearch.value = event.cliente?.nome || String(event.cliente_id || '')
      selectedClienteNome.value = event.cliente?.nome || String(event.cliente_id || '')
      statusForm.value = {
        status: event.status || 'AGENDADO',
        motivo: event.motivo || '',
        observacoes: '',
      }
    } else {
      selectedEvent.value = null
      eventForm.value = {
        cliente_id: undefined,
        tipo: 'NEGOCIACAO',
        data_agendamento: '',
        atendente_id: isNotExecutivoCobranca.value ? undefined : usuarioLogadoId,
        observacoes: '',
        status: 'AGENDADO',
        motivo: '',
      }
      clienteSearch.value = ''
      selectedClienteNome.value = ''
      statusForm.value = { status: '', motivo: '', observacoes: '' }
    }
    clienteSuggestions.value = []
    showClienteSuggestions.value = false
    highlightedClienteIndex.value = -1
    isEventSidebarOpen.value = true
  }

  const handleDayClick = day => {
    const d = day.fullDate || new Date(day.year, day.month, day.date)
    d.setHours(9, 0, 0, 0)
    openEventSidebar()
    eventForm.value.data_agendamento = formatDateTimeForPicker(d)
  }

  const handleTimeSlotClick = (day, hour) => {
    const d = day.fullDate ? new Date(day.fullDate) : new Date(day.year, day.month, day.date)
    d.setHours(hour, 0, 0, 0)
    openEventSidebar()
    eventForm.value.data_agendamento = formatDateTimeForPicker(d)
  }

  const handleSaveEvent = async () => {
    if (!eventForm.value.cliente_id) {
      toast({ component: ToastificationContent, props: { title: 'Selecione um cliente', icon: 'AlertTriangleIcon', variant: 'danger' } })
      return
    }
    if (!eventForm.value.data_agendamento) {
      toast({ component: ToastificationContent, props: { title: 'Selecione uma data', icon: 'AlertTriangleIcon', variant: 'danger' } })
      return
    }

    saving.value = true
    try {
      const dateObj = parseDateFromPicker(eventForm.value.data_agendamento)
      if (!dateObj) throw new Error('Data inválida')

      const dataAgendamentoISO = convertLocalToUTC(dateObj)

      if (selectedEvent.value?.id) {
        await agendamentosService.reagendarAgendamento(selectedEvent.value.id, {
          data_agendamento: dataAgendamentoISO,
          observacoes: eventForm.value.observacoes,
          status: eventForm.value.status,
          motivo: eventForm.value.motivo || null,
        })
        toast({ component: ToastificationContent, props: { title: 'Agendamento reagendado', icon: 'CheckIcon', variant: 'success' } })
      } else {
        await agendamentosService.criarAgendamento({
          cliente_id: eventForm.value.cliente_id,
          atendente_id: eventForm.value.atendente_id || usuarioLogadoId,
          tipo: eventForm.value.tipo || 'NEGOCIACAO',
          data_agendamento: dataAgendamentoISO,
          observacoes: eventForm.value.observacoes || '',
          status: eventForm.value.status || 'AGENDADO',
          motivo: eventForm.value.motivo || null,
        })
        toast({ component: ToastificationContent, props: { title: 'Agendamento criado', icon: 'CheckIcon', variant: 'success' } })
      }

      isEventSidebarOpen.value = false
      await fetchAgendamentos()
    } catch (error) {
      toast({ component: ToastificationContent, props: { title: 'Erro', icon: 'AlertTriangleIcon', variant: 'danger', text: error.message } })
    } finally {
      saving.value = false
    }
  }

  const handleDeleteEvent = async () => {
    if (!selectedEvent.value?.id) return
    // eslint-disable-next-line no-restricted-globals, no-alert
    if (!confirm('Tem certeza que deseja excluir este agendamento?')) return
    // eslint-disable-next-line no-restricted-globals, no-alert
    const motivo = prompt('Digite o motivo do cancelamento:')

    try {
      await agendamentosService.excluirAgendamento(selectedEvent.value.id, motivo)
      toast({ component: ToastificationContent, props: { title: 'Agendamento excluído', icon: 'CheckIcon', variant: 'success' } })
      isEventSidebarOpen.value = false
      await fetchAgendamentos()
    } catch {
      toast({ component: ToastificationContent, props: { title: 'Erro ao excluir', icon: 'AlertTriangleIcon', variant: 'danger' } })
    }
  }

  const handleUpdateStatus = async () => {
    if (!selectedEvent.value?.id || !statusForm.value.status) return
    updatingStatus.value = true
    try {
      await agendamentosService.atualizarStatusAgendamento(
        selectedEvent.value.id,
        statusForm.value.status,
        statusForm.value.motivo || null,
        statusForm.value.observacoes || null,
      )
      toast({ component: ToastificationContent, props: { title: 'Status atualizado', icon: 'CheckIcon', variant: 'success' } })
      statusForm.value.motivo = ''
      statusForm.value.observacoes = ''
      await fetchAgendamentos()
    } catch {
      toast({ component: ToastificationContent, props: { title: 'Erro ao atualizar status', icon: 'AlertTriangleIcon', variant: 'danger' } })
    } finally {
      updatingStatus.value = false
    }
  }

  // ------------------------------------------------
  // Lifecycle & watchers
  // ------------------------------------------------
  onMounted(() => {
    fetchAgendamentos()
    if (isNotExecutivoCobranca.value) {
      fetchExecutivos()
    }
  })

  watch([currentDate, view], () => {
    fetchAgendamentos()
  })

  return {
    // State
    currentDate,
    view,
    loading,
    agendamentos,
    selectedTipos,
    selectedStatuses,
    selectedMotivos,
    showAllMotivos,
    selectedExecutivos,
    isEventSidebarOpen,
    selectedEvent,
    eventForm,
    statusForm,
    saving,
    updatingStatus,
    usuarioLogadoId,
    isNotExecutivoCobranca,

    // Client search
    clienteSearch,
    clienteSuggestions,
    highlightedClienteIndex,
    isSearchingClientes,
    showClienteSuggestions,
    showClienteSearchDropdown: computed(() => !selectedEvent.value && clienteSearch.value.length >= 3 && !eventForm.value.cliente_id),
    handleClienteSearchFocus,
    handleClienteSearchBlur,
    navigateClienteDown,
    navigateClienteUp,
    selectHighlightedCliente,
    selectCliente,
    clearClienteSelection,

    // Computed
    currentMonthYear,
    weekDays,
    calendarDays,
    weekViewDays,
    filteredEvents,
    sortedEvents,
    executivosComAgendamentos,
    executivoOptionsForForm,

    // Methods
    previousMonth,
    nextMonth,
    goToToday,
    openDayView,
    toggleTipo,
    toggleStatus,
    toggleMotivo,
    clearMotivos,
    uncheckAllMotivos,
    toggleExecutivo,
    getEventsForDay,
    getEventsForHour,
    getEventsForDayHour,
    openEventSidebar,
    handleDayClick,
    handleTimeSlotClick,
    handleSaveEvent,
    handleDeleteEvent,
    handleUpdateStatus,
    fetchAgendamentos,
  }
}
